import { use, useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router";
import { useUserData } from "@/components/user/lib/hooks/useUserData";
import { AuthContext } from "@/context/AuthContext";
import { useLocalStorage } from "@/hooks/useLocalStorage";
import { HttpClient } from "@/services/HttpClient";
import { PREMIUM_ROLES, type UserRole } from "@/services/user-auth/user-roles";
import { MAX_ATTEMPTS_TO_CONFIRM_PHONE } from "@/shared/components/PhoneNumberForm";
import { LocalStorageKeys } from "@/shared/types/common";
import type { UserStatus } from "../../types";

export function useDefineUserStatus() {
	const navigate = useNavigate();
	const context = use(AuthContext);

	const { userData } = useUserData();
	const [searchParams] = useSearchParams();
	const redirectFrom = searchParams.get("redirect");
	const [redirectPath] = useLocalStorage(LocalStorageKeys.redirectPath, null);
	const [attemptToBuy] = useLocalStorage(LocalStorageKeys.attemptToBuy, null);
	const attemptToConfirmPhone =
		Number(sessionStorage.getItem(LocalStorageKeys.attemptToConfirmPhone)) || 0;
	const [userStatus, setUserStatus] = useState<UserStatus | null>(null);
	const isAllowedToConfirmPhone =
		Number(attemptToConfirmPhone) < MAX_ATTEMPTS_TO_CONFIRM_PHONE;

	useEffect(() => {
		if (redirectPath) navigate(redirectPath);
	}, [navigate, redirectPath]);

	// biome-ignore lint/correctness/useExhaustiveDependencies: we don't need Context and setUserStatus in the dependency array
	const updateUserStatus = useCallback(async () => {
		if (!userData || !context?.isAuthenticated) return;

		const res = await HttpClient.refreshAccessToken();
		const newRoles = context.getRolesFromToken(res.access_token);
		context.updateUserRoles(newRoles);

		const isPremium = newRoles.some((role: UserRole) =>
			PREMIUM_ROLES.includes(role),
		);

		// User just passed the registration (flow#1)
		if (!userData.termsConfirmed && !attemptToBuy) {
			setUserStatus("registered");
			return;
		}

		// User have bought a Premium plan was redirected from Stripe (flow#2)
		if (
			redirectFrom === "stripe" &&
			isPremium &&
			!attemptToBuy &&
			isAllowedToConfirmPhone
		) {
			setUserStatus("upgradedPlan");
			return;
		}
		// If a Premium user didn't confirm a phone number
		if (
			isPremium &&
			userData.termsConfirmed &&
			!userData.phoneConfirmed &&
			isAllowedToConfirmPhone
		) {
			setUserStatus("notConfirmedPhone");
			return;
		}

		// Unauthenticated user who attempted to buy a Premium plan (flow#3)
		if (
			isPremium &&
			attemptToBuy &&
			!userData.phoneConfirmed &&
			isAllowedToConfirmPhone
		) {
			setUserStatus("unAuthenticatedAttemptedToBuy");
			return;
		}

		setUserStatus(null);
	}, [userData, redirectFrom, attemptToBuy, isAllowedToConfirmPhone]);

	useEffect(() => {
		updateUserStatus();
	}, [updateUserStatus]);

	if (!context?.isAuthenticated) {
		return {
			userStatus: null,
			setUserStatus: () => {},
		};
	}

	return {
		userStatus,
		setUserStatus,
	};
}
